import React, { useEffect, useMemo, useState } from "react";
import { Select, Spin } from "antd";
import { LDS_AUTOCMPLETE_CITIES } from "../../../assets/Data/LdsAirportsAutocomplete";
import { getActiveDestinations } from "../../../services/fhpService";

function normalizeApiList(res) {
  if (res == null) return [];
  if (Array.isArray(res)) return res;
  if (res && Array.isArray(res.data)) return res.data;
  if (typeof res === "object" && res.error) return [];
  // SOAP→JSON לעיתים מחזיר יעד יחיד כאובייקט במקום מערך
  if (typeof res === "object" && (res.destCity != null || res.dealType != null || res.CITYCODE != null)) {
    return [res];
  }
  return [];
}

function optionsFromActiveDestinations(list) {
  const fh = (list || []).filter((d) => d?.dealType === "FlightsHotel");
  const mapped = fh.map((city) => {
    const code = String(city.destCity || city.CITYCODE || "").trim();
    const heb = LDS_AUTOCMPLETE_CITIES.find(
      (c) => (c.AIRPORTCODE || c.CITYCODE) === code
    );
    const title = heb?.CITYNAME_HEB || city.destCityName || city.CITYNAME || code;
    const country = heb?.COUNTRYNAME_HEB || city.destCountryName || city.COUNTRYNAME || "";
    return {
      value: code,
      label: `${title} (${code})${country ? ` — ${country}` : ""}`,
    };
  });
  const seen = new Set();
  const out = [];
  for (const m of mapped) {
    if (!m.value || m.value === "ALL" || seen.has(m.value)) continue;
    seen.add(m.value);
    out.push(m);
  }
  out.sort((a, b) => (a.label || "").localeCompare(b.label || "", "he"));
  return out;
}

function optionsFromStaticCatalog() {
  const out = [];
  const seen = new Set();
  for (const d of LDS_AUTOCMPLETE_CITIES) {
    const code = String(d.AIRPORTCODE || d.CITYCODE || "").trim();
    if (!code || code === "ALL" || seen.has(code)) continue;
    seen.add(code);
    const title = d.CITYNAME_HEB || d.CITYNAME || code;
    const country = d.COUNTRYNAME_HEB || d.COUNTRYNAME || "";
    out.push({
      value: code,
      label: `${title} (${code})${country ? ` — ${country}` : ""}`,
    });
  }
  out.sort((a, b) => (a.label || "").localeCompare(b.label || "", "he"));
  return out;
}

/**
 * יעדי חבילות (FlightsHotel) לפי getActiveDestinations מ-LDS; נפילה לקטלוג הסטטי אם ה-API נכשל.
 * ערך הטופס: קוד יעד בלבד (כמו searchLds.city).
 */
const AiSalesDestinationSelect = React.forwardRef(function AiSalesDestinationSelect(
  { value, onChange, placeholder, disabled, allowClear = true, ...rest },
  ref
) {
  const [loading, setLoading] = useState(true);
  const [baseOptions, setBaseOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getActiveDestinations()
      .then((res) => {
        const list = normalizeApiList(res);
        const opts = optionsFromActiveDestinations(list);
        if (!cancelled) {
          setBaseOptions(opts.length > 0 ? opts : optionsFromStaticCatalog());
        }
      })
      .catch(() => {
        if (!cancelled) setBaseOptions(optionsFromStaticCatalog());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => {
    const v = value != null ? String(value).trim() : "";
    if (!v) return baseOptions;
    if (baseOptions.some((o) => o.value === v)) return baseOptions;
    return [
      { value: v, label: `${v} (נשמר בליד — לא ברשימה הפעילה)` },
      ...baseOptions,
    ];
  }, [baseOptions, value]);

  return (
    <Select
      ref={ref}
      showSearch
      allowClear={allowClear}
      disabled={disabled}
      loading={loading}
      value={value || undefined}
      onChange={onChange}
      placeholder={placeholder || "בחר יעד מרשימת LDS הפעילה"}
      options={options}
      optionFilterProp="label"
      notFoundContent={loading ? <Spin size="small" /> : "לא נמצא"}
      popupMatchSelectWidth={false}
      style={{ width: "100%" }}
      {...rest}
    />
  );
});

export default AiSalesDestinationSelect;
